<template>
  <v-dialog
    v-model="isOpen"
    max-width="500"
    persistent
    transition="fade-transition"
    @click:outside="handleCancel"
  >
    <v-card class="move-modal">
      <!-- Content -->
      <div class="modal-content">
        <div class="modal-text">
          <h3 class="modal-title">Move to another cart</h3>
          <p class="modal-description">
            You can move this activity to the other cart, or move to the new
            cart
          </p>
        </div>

        <!-- Cart Selection -->
        <div class="cart-selection">
          <div
            v-for="cart in cartsToShow"
            :key="cart.value"
            class="cart-option"
            @click="selectCart(cart.value)"
          >
            <div class="radio-button">
              <div
                class="radio-circle"
                :class="{ 'radio-selected': selectedCart === cart.value }"
              >
                <div v-if="selectedCart === cart.value" class="radio-dot"></div>
              </div>
            </div>
            <div class="cart-name">{{ cart.text }}</div>
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <ActionButton
          variant="secondary"
          @click="handleCancel"
          class="modal-button"
          :disabled="loading"
        >
          Back
        </ActionButton>
        <ActionButton
          variant="primary"
          @click="handleMove"
          class="modal-button"
          :disabled="!selectedCart"
          :loading="loading"
        >
          Move Activity
        </ActionButton>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  availableCarts: {
    type: Array,
    default: () => [],
  },
  currentCartId: {
    type: [String, Number],
    default: null,
  },
  activityData: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "move", "cancel"]);

const selectedCart = ref(null);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const cartsToShow = computed(() => {
  return (props.availableCarts || []).filter(
    (c) => c.value === "create_new" || c.value !== props.currentCartId
  );
});

const selectCart = (cartValue) => {
  selectedCart.value = cartValue;
};

const handleMove = () => {
  if (selectedCart.value) {
    emit("move", {
      targetCartId: selectedCart.value,
      activityData: props.activityData,
    });
  }
};

const handleCancel = () => {
  emit("cancel");
  isOpen.value = false;
};

// Reset selection when modal opens
watch(isOpen, (newValue) => {
  if (newValue) {
    selectedCart.value = null;
  }
});
</script>

<style scoped>
.move-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  box-shadow: none;
  border: none;
}

/* Fade transition animations - smooth and jitter-free */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Prevent jittering by ensuring smooth transitions */
:deep(.v-dialog) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

:deep(.v-overlay__content) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Ensure modal content doesn't cause layout shifts */
.move-modal {
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  position: relative;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.modal-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.modal-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.024px;
}

.modal-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  margin: 0;
  letter-spacing: 0.08px;
}

.cart-selection {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.cart-option {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 8px;
}

.cart-option:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.radio-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.radio-circle {
  width: 24px;
  height: 24px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.radio-selected {
  border-color: var(--primary-blue);
  background-color: var(--primary-blue);
}

.radio-dot {
  width: 8px;
  height: 8px;
  background-color: white;
  border-radius: 50%;
}

.cart-name {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
  flex: 1;
}

.modal-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.modal-button {
  flex: 1;
  height: 56px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .move-modal {
    padding: 20px;
    gap: 24px;
  }

  .modal-actions {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .modal-button {
    width: 100%;
  }

  :deep(.modal-button .action-button__text) {
    font-size: 14px;
  }
}
</style>
